import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PixZentLogo } from '../landing/Header';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <PixZentLogo className="w-8 h-8" />
            <span className="text-xl font-bold text-slate-900">PixZent</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-violet-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        
        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using the PixZent Technologies website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Services Description</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              PixZent provides AI visibility optimization services, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>AI Search Optimization (AEO) - Optimization for ChatGPT, Perplexity, and other AI assistants</li>
              <li>Generative Engine Optimization (GEO) - Optimization for AI-generated search responses</li>
              <li>Traditional Search Engine Optimization (SEO)</li>
              <li>Local SEO and Google My Business optimization</li>
              <li>AI Visibility Audits and Reports</li>
              <li>Strategy consulting and implementation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. User Accounts</h2>
            <p className="text-slate-600 leading-relaxed">
              When you create an account or submit information through our forms, you are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You agree to provide accurate, current, and complete information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Free Audit Service</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our free 48-hour AI visibility audit is provided as a complimentary service to help you understand your current digital presence. By requesting a free audit:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>You consent to us analyzing your website and online presence</li>
              <li>You agree to receive the audit report via the email provided</li>
              <li>You understand that the audit is for informational purposes and does not guarantee specific results</li>
              <li>You agree that we may contact you regarding our services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Payment Terms</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              For paid services:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>All fees are quoted in the currency specified in your proposal or invoice</li>
              <li>Payment terms will be specified in your service agreement</li>
              <li>Late payments may result in suspension of services</li>
              <li>Refunds are subject to our refund policy as outlined in your service agreement</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              All content on our website, including text, graphics, logos, and software, is the property of PixZent Technologies and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Client Responsibilities</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              When engaging our services, clients agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide accurate and complete information about their business</li>
              <li>Grant necessary access to websites, analytics, and other platforms as required</li>
              <li>Respond to requests for information in a timely manner</li>
              <li>Review and approve content and strategies before implementation</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Results Disclaimer</h2>
            <p className="text-slate-600 leading-relaxed">
              While we strive to deliver excellent results, we cannot guarantee specific rankings, traffic levels, or business outcomes. AI algorithms and search engines are constantly evolving, and results may vary based on numerous factors outside our control. Past performance and case studies do not guarantee future results.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              To the maximum extent permitted by law, PixZent Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Confidentiality</h2>
            <p className="text-slate-600 leading-relaxed">
              We treat all client information as confidential. We will not disclose your business information to third parties except as necessary to provide our services or as required by law. This confidentiality obligation survives the termination of our business relationship.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              Either party may terminate services as specified in the service agreement. We reserve the right to terminate or suspend access to our services immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page. Your continued use of our services after any changes indicates your acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">14. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-slate-700"><strong>PixZent Technologies</strong></p>
              <p className="text-slate-600">Email: dmb@pixzent.com</p>
              <p className="text-slate-600">Phone: +971508541002</p>
              <p className="text-slate-600">Location: Dubai, UAE</p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
